import { useCallback } from 'react';
import { Checkbox, Typography, Box, Stack } from 'components/base';
import useManagePermission from 'pages/ProjectBudget/DayworkDocket/ResourceMapping/common/hooks/useManagePermission';
import ResourceInfoWithTooltip from 'components/__v5__/ResourceAutocomplete/common/ResourceInfoWithTooltip';
import { IResourceInfo } from 'pages/ProjectBudget/DayworkDocket/ResourceMapping/common/api/types';
import { CheckboxIcon } from '../CheckboxIcon/CheckboxIcon';
import { RenderOptionProps } from '../../types/types';
import {
  IArg,
  renderEquipmentCustomLabel,
} from './utils/RenderEquipmentCustomLabel';
import { TruncatedLabel } from './utils/TruncatedLabel';

export function RenderOption<T>({
  option,
  state,
  props,
  getOptionLabel,
  multiple,
  showEquipmentCustomLabel,
  showLibraryResourceCustomLabel,
  showProjectCustomLabel,
  noWrapLabel = true,
}: Readonly<RenderOptionProps<T>>) {
  const { canChangeEquipmentHireType } = useManagePermission();
  const labelText = getOptionLabel ? getOptionLabel(option) : '';

  const CustomLabel = useCallback(
    () => <TruncatedLabel text={labelText} noWrap={noWrapLabel} />,
    [labelText, noWrapLabel],
  );

  // Memoized renderCustomLabel to avoid unnecessary re-creation
  const renderCustomLabel = useCallback(() => {
    if (showEquipmentCustomLabel) {
      return renderEquipmentCustomLabel(
        option as unknown as IArg,
        canChangeEquipmentHireType,
      );
    }

    if (showLibraryResourceCustomLabel) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <CustomLabel />
          <ResourceInfoWithTooltip
            resourceInfo={option as unknown as IResourceInfo}
          />
        </Box>
      );
    }

    if (showProjectCustomLabel) {
      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={0.5}
        >
          <CustomLabel />
          {(option as unknown as { status: number })?.status === 3 && (
            <Typography variant="body2" py={0.4} color="success">
              (Completed)
            </Typography>
          )}
        </Stack>
      );
    }

    return <CustomLabel />;
  }, [
    showEquipmentCustomLabel,
    showLibraryResourceCustomLabel,
    showProjectCustomLabel,
    CustomLabel,
    option,
    canChangeEquipmentHireType,
  ]);

  return (
    <li {...props} key={`option-${(option as { id: string }).id}-${props.id}`}>
      {multiple && (
        <Checkbox
          icon={CheckboxIcon.icon}
          checkedIcon={CheckboxIcon.checkedIcon}
          style={{ marginRight: 2 }}
          checked={state.selected}
          size="small"
        />
      )}
      {renderCustomLabel()}
    </li>
  );
}
